/**
 * Collection Product Slider 
 * Enhanced slideshow handling for product cards in collection views
 */

class CollectionProductSlider {
  constructor() {
    this.initSliders();
    this.setupEventListeners();
  }

  initSliders() {
    const slideshows = document.querySelectorAll('.product-slideshow:not(.slider-initialized)');
    
    slideshows.forEach(slideshow => {
      slideshow.classList.add('slider-initialized');
      this.setupSlider(slideshow);
    });
  }

  setupEventListeners() {
    // Re-initialize on Shopify section changes
    document.addEventListener('shopify:section:load', () => this.initSliders());
    document.addEventListener('shopify:block:select', () => this.initSliders());
    
    // Listen for color swatch changes
    document.addEventListener('variantImageFiltered', (event) => {
      if (event.target && event.target.classList.contains('product-slideshow')) {
        this.handleVariantChange(event.target, event.detail);
      }
    });
    
    // Initialize any new sliders added to the DOM
    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;
      
      mutations.forEach(mutation => {
        if (mutation.addedNodes && mutation.addedNodes.length) {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (node.nodeType === 1 && (
                node.classList?.contains('product-slideshow') || 
                node.querySelector?.('.product-slideshow')
            )) {
              shouldInit = true;
              break;
            }
          }
        }
      });
      
      if (shouldInit) {
        this.initSliders();
      }
    });
    
    // Observe the entire document for new sliders
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  setupSlider(slideshow) {
    const slides = slideshow.querySelectorAll('.product-slideshow__slide');
    if (!slides.length) return;
    
    // Store references to DOM elements
    slideshow._slides = slides;
    
    // Find navigation elements
    const productCard = slideshow.closest('.card');
    if (!productCard) return;
    
    const prevButton = productCard.querySelector('.prev-slide');
    const nextButton = productCard.querySelector('.next-slide');
    const indicators = productCard.querySelectorAll('.product-slideshow__navigation button');
    
    slideshow._prevButton = prevButton;
    slideshow._nextButton = nextButton;
    slideshow._indicators = indicators;
    slideshow._currentIndex = 0;
    
    // Set up event handlers
    if (prevButton) {
      prevButton.removeEventListener('click', prevButton.clickHandler);
      prevButton.clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.navigateSlider(slideshow, 'prev');
      };
      prevButton.addEventListener('click', prevButton.clickHandler);
    }
    
    if (nextButton) {
      nextButton.removeEventListener('click', nextButton.clickHandler);
      nextButton.clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.navigateSlider(slideshow, 'next');
      };
      nextButton.addEventListener('click', nextButton.clickHandler);
    }
    
    // Set up indicator buttons
    if (indicators && indicators.length) {
      indicators.forEach((indicator, index) => {
        indicator.removeEventListener('click', indicator.clickHandler);
        indicator.clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToSlide(slideshow, index);
        };
        indicator.addEventListener('click', indicator.clickHandler);
      });
    }
    
    // Initialize the first slide
    this.goToSlide(slideshow, 0);
  }

  navigateSlider(slideshow, direction) {
    const slides = slideshow._slides;
    const currentIndex = slideshow._currentIndex;
    const visibleSlides = Array.from(slides).filter(slide => slide.style.display !== 'none');
    
    if (!visibleSlides.length) return;
    
    // Find the current slide in the list of visible slides
    const currentVisibleIndex = visibleSlides.findIndex(slide => 
      slide === Array.from(slides)[currentIndex]);
    
    let newVisibleIndex;
    if (direction === 'prev') {
      newVisibleIndex = (currentVisibleIndex > 0) ? currentVisibleIndex - 1 : visibleSlides.length - 1;
    } else {
      newVisibleIndex = (currentVisibleIndex < visibleSlides.length - 1) ? currentVisibleIndex + 1 : 0;
    }
    
    // Get the actual slide index from the full slides array
    const newIndex = Array.from(slides).indexOf(visibleSlides[newVisibleIndex]);
    this.goToSlide(slideshow, newIndex);
  }

  goToSlide(slideshow, index) {
    const slides = slideshow._slides;
    const indicators = slideshow._indicators;
    
    // If target slide is hidden, find the next visible one
    let targetIndex = index;
    const maxAttempts = slides.length;
    let attempts = 0;
    
    while (attempts < maxAttempts && slides[targetIndex].style.display === 'none') {
      targetIndex = (targetIndex + 1) % slides.length;
      attempts++;
    }
    
    // Update current index
    slideshow._currentIndex = targetIndex;
    
    // Update slide visibility
    Array.from(slides).forEach((slide, i) => {
      slide.classList.toggle('active', i === targetIndex);
    });
    
    // Update indicators if any
    if (indicators && indicators.length) {
      Array.from(indicators).forEach((indicator, i) => {
        indicator.classList.toggle('active', i === targetIndex);
      });
    }
  }

  handleVariantChange(slideshow, detail) {
    if (!detail || !detail.visibleSlides || !detail.visibleSlides.length) return;
    
    // Go to the first visible slide when variant changes
    const firstVisibleIndex = Array.from(slideshow._slides).indexOf(detail.visibleSlides[0]);
    if (firstVisibleIndex >= 0) {
      this.goToSlide(slideshow, firstVisibleIndex);
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  window.collectionProductSlider = new CollectionProductSlider();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionProductSlider;
}
